/**
 * <AttachmentBlock> — visible inline badge for a SPA-attached file or
 * petition-selection payload. Previously these rode invisibly in the
 * server-side preamble; the rich-content-model redesign makes them
 * first-class blocks in the user's message bubble.
 *
 * **No live callers today (intentional scaffolding).** The outbound
 * composer still sends attachments via `chat.send`'s side-channel
 * `attachments?: ChatAttachment[]` (the server-side `before_prompt_build`
 * hook reshapes them into the preamble), so no inbound shape produces
 * `attachment` blocks yet. This component is wired up + tested at
 * compile time by the BlockRenderer dispatcher; first live caller will
 * be the outbound-composer redesign slice (plan §6 followup).
 *
 * v1 surfaces the attachment kind + a compact label. Click-to-preview
 * (open the source range in the WorkspaceFilePane via the existing
 * `lx_open_file` flow) is a followup.
 */
import { Paperclip } from "lucide-react";
import type { AttachmentBlock as AttachmentBlockType } from "@/types/content-blocks";

interface AttachmentBlockProps {
  block: AttachmentBlockType;
}

export function AttachmentBlock({ block }: AttachmentBlockProps) {
  const a = block.attachment;
  // Today only `petition-selection` exists; the inferred shape gives a
  // useful label (doc id + line range). Add cases here as new
  // attachment kinds ship.
  let label = "Attachment";
  if (a.type === "petition-selection") {
    label = `Petition lines ${a.metadata.startLine}–${a.metadata.endLine}`;
  }
  return (
    <div
      data-testid="attachment-block"
      data-attachment-type={a.type}
      className="my-1 inline-flex items-center gap-1.5 rounded border border-cream-300 bg-cream-100/30 px-2 py-1 font-sans text-caption text-coffee-700 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300"
    >
      <Paperclip size={12} />
      <span>{label}</span>
    </div>
  );
}
